/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { MouseEvent } from "react";
import { Input, Text, theme } from "@nordcloud/gnui";
import { SidebarSelector } from "~/components/SidebarWithMultiSelect/SidebarSelector";
import {
  getFirstOptionLabel,
  getMultiSelectLabel,
} from "~/components/SidebarWithMultiSelect/utils";
import { useDisclosure, useGetNotificationGroupsAsSidebarItems } from "~/hooks";
import { NotificationGroupSidebarItem } from "./NotificationGroupSidebarItem";

type Props = {
  onSubmit: (ids: string[]) => void;
  onClear: () => void;
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
};

export function NotificationGroupSidebar({
  onSubmit,
  onClear,
  selectedOptions,
  setSelectedOptions,
}: Props) {
  const { isOpen, close, open } = useDisclosure();

  const { options, loading } = useGetNotificationGroupsAsSidebarItems();

  const handleItemSelect = (selectedItem: string) => {
    if (selectedOptions.includes(selectedItem)) {
      setSelectedOptions((selectedItems) =>
        selectedItems.filter((item) => item !== selectedItem)
      );
    } else {
      setSelectedOptions((selectedItems) => [...selectedItems, selectedItem]);
    }
  };
  const handleSubmit = () => {
    onSubmit(selectedOptions);
    close();
  };

  const handleClose = () => {
    setSelectedOptions(selectedOptions);
    close();
  };

  const selectedCount = selectedOptions.length;

  const firstOptionLabel =
    selectedCount === 1 && getFirstOptionLabel(selectedOptions, options);

  const multiSelectLabel =
    selectedCount > 1 &&
    getMultiSelectLabel(
      selectedCount,
      "Notification Group",
      "Notification Groups"
    );

  const handleClear = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onClear();
    setSelectedOptions([]);
  };

  return (
    <>
      <div css={{ marginTop: theme.spacing.spacing03 }}>
        <Text>{`Notification Recipient(s):`}</Text>
        <Input
          aria-label="Select Notification Group"
          onClick={open}
          placeholder="Select Recipients"
          readOnly
          value={firstOptionLabel || multiSelectLabel || ""}
          icon="sidebar"
          showClearButton={selectedCount > 0}
          onClear={handleClear as () => void}
        />
      </div>
      <SidebarSelector
        name="Notification Groups"
        title="Notification Groups"
        sidebarItem={NotificationGroupSidebarItem}
        isOpen={isOpen}
        onClose={handleClose}
        showValueInOptions
        options={options}
        selectedOptions={selectedOptions}
        onItemSelect={handleItemSelect}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}
